/*
  as-fixes.js - video loading from the 09.10.2026 speed audit. Loaded (deferred) from layout/theme.liquid.

  1. video[data-deferred-src] - the home-page hero (sections/image-with-video.liquid). It gets its src
     only once the page has finished loading, so the 3.2 MB file no longer competes with the page for
     bandwidth; the poster frame shows until then. Visitors who asked for reduced motion or data saving
     keep the poster.

  2. video[data-lazy-autoplay] - autoplaying videos rendered by snippets/as-light-video.liquid. Their
     <source data-src> is filled in when the video comes within ~300 px of the viewport, so videos further
     down the page, and copies hidden by CSS on the other screen size, are not downloaded up front.

  3. Collection grid on phones: product-card images carry a sizes hint for the current one/two-column layout
     (sections/main-collection.liquid). When the layout switch is used, the hint follows, and the browser
     picks the matching image width again.
*/
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const holdBack = reducedMotion || saveData;

  function play(video) {
    const result = video.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
  }

  function whenIdle(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 2000 });
    } else {
      setTimeout(callback, 200);
    }
  }

  function startDeferredVideos() {
    if (holdBack) return;
    document.querySelectorAll('video[data-deferred-src]').forEach((video) => {
      video.src = video.dataset.deferredSrc;
      video.removeAttribute('data-deferred-src');
      if (video.hasAttribute('autoplay')) play(video);
    });
  }

  function startLazyVideo(video) {
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.removeAttribute('data-lazy-autoplay');

    if (holdBack) {
      // Show the first frame, but do not start the motion.
      video.preload = 'metadata';
      video.load();
      return;
    }

    video.load();
    play(video);
  }

  let observer = null;

  function watchLazyVideos(root) {
    const videos = root.querySelectorAll('video[data-lazy-autoplay]');
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach(startLazyVideo);
      return;
    }

    observer =
      observer ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            startLazyVideo(entry.target);
          });
        },
        { rootMargin: '300px 0px' }
      );

    videos.forEach((video) => observer.observe(video));
  }

  if (document.readyState === 'complete') {
    whenIdle(startDeferredVideos);
  } else {
    window.addEventListener('load', () => whenIdle(startDeferredVideos), { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => watchLazyVideos(document));
  } else {
    watchLazyVideos(document);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('collection-layout-switch[device="mobile"] button[value]');
    if (!button) return;
    const list = document.getElementById(button.closest('collection-layout-switch').getAttribute('aria-controls'));
    if (!list) return;
    const mobile = button.value === 'large' ? '92vw' : '50vw';
    list.querySelectorAll('img.product-card__image').forEach((img) => {
      img.sizes = img.sizes.replace(/\(max-width: 699px\) \d+vw/, `(max-width: 699px) ${mobile}`);
    });
  });

  // Theme editor: re-scan a section after it is re-rendered.
  document.addEventListener('shopify:section:load', (event) => {
    watchLazyVideos(event.target);
    startDeferredVideos();
  });
})();
